import React from "react";
import { DownloadCloud, Fingerprint, FolderCheck, ScanLine } from "lucide-react";

const CARDS = [
  {
    icon: DownloadCloud,
    title: "Driver capture",
    body: "Capture drivers from the first known-good device or validated workflow.",
  },
  {
    icon: Fingerprint,
    title: "Hardware fingerprinting",
    body: "Match devices on more than model name — hardware IDs, SKU, BIOS, Wi-Fi chipset, storage controller, GPU, and configuration.",
  },
  {
    icon: FolderCheck,
    title: "Known-good profiles, stored locally",
    body: "Mark successful driver and image combinations as known-good, store them locally, and reuse them across matching devices in a batch — no repeated setup.",
  },
  {
    icon: ScanLine,
    title: "Post-image verification",
    body: "Confirm required drivers are installed after reimaging.",
  },
];

const DriverIntelligence = () => {
  return (
    <section id="drivers" className="relative bg-brand-bg font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            Driver intelligence
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            Capture Once. Reuse Locally.{" "}
            <span className="text-brand-gradient">Verify Every Time.</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink sm:text-lg">
            Furloop reduces repeated driver work by capturing known-good driver profiles and reusing them for
            matching devices.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="flex flex-col rounded-2xl border border-line bg-white p-6 transition-all hover:-translate-y-1 hover:border-brand-orchid hover:shadow-xl hover:shadow-brand-plum/5"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-wash text-brand-magenta ring-1 ring-brand-orchid/30">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-semibold text-brand-plum">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-brand-orchid/30 bg-gradient-to-r from-brand-wash to-white p-6 sm:p-8">
          <p className="font-display text-lg font-medium text-brand-plum sm:text-xl">
            “The first device teaches the workflow.{" "}
            <span className="text-brand-magenta">The second matching device moves faster.”</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default DriverIntelligence;
